import { z } from "zod/v4";

import { PageAnalysisSchema } from "./shopify";

/**
 * Which rules wrote a page read. Rides every read, so a stored card can tell
 * which grain, `about` and `describe` rules produced what it quotes. Raise it
 * when any of them change.
 */
export const PAGE_READ_VERSION = 7;

/** A script-drawn block the read recognises by its vendor's markup. */
export const PageWidgetList = [
  "yotpo",
  "okendo",
  "trustpilot",
  "reviewsio",
  "judgeme",
  "stamped",
  "junip",
  "loox",
  "frontrow",
  "recommendations",
  "faq",
] as const;

export const PageWidgetSchema = z.enum(PageWidgetList);

/**
 * Page-local, like `e12`: the page's own content in document order, then each
 * popup outside a section.
 */
export const PageElementIdSchema = z.string().regex(/^e\d+$/);

/** The place label of a page builder's content outside any section. */
export const PAGE_CONTENT_LABEL = "the page's own content";

/** A popup's place label. */
export const popupLabel = (kind: string) => `a ${kind} popup`;

/** Where an element sits, in words: "in the hero section", "in the page's own content", "in a discount popup". */
export function inPlace(label: string): string {
  return label === PAGE_CONTENT_LABEL ||
    /^a .+ popup( \d+ of \d+)?$/.test(label) ||
    /^section \d+ of \d+$/.test(label)
    ? `in ${label}`
    : `in the ${label} section`;
}

/**
 * How a reader finds one element in words: what it says, then where it sits,
 * stopping at the first part that fits exactly one element.
 */
export const DescriptionSchema = z.object({
  /** Its own words, or a picture's file. */
  quote: z.string(),
  /** The section it sits in. */
  section: z.string(),
  /** The product card it sits in, when one was needed. */
  card: z.string().nullable(),
  /** The quotes of its neighbours, only when needed. */
  before: z.string().nullable(),
  after: z.string().nullable(),
  /**
   * What sets it apart inside its own block, when its words repeat elsewhere:
   * the first words of the block no other match's block holds, or its link.
   * Checked to read back to this element. Null when nothing was needed.
   */
  within: z
    .object({ type: z.enum(["quote", "link"]), value: z.string() })
    .nullable(),
  /** Its place among the quote's readers in its block, when no anchor reads back. */
  nth: z.object({ n: z.number(), of: z.number() }).nullable(),
  /**
   * How many elements the description names on purpose: 1, or every copy of a
   * block a page draws more than once, like a marquee, a looping slider, or a
   * theme's phone and desktop menus.
   */
  copies: z.number(),
  /** Elements in this read the description fits. Good when it equals `copies`. */
  matches: z.number(),
});

export const PageElementSchema = z.object({
  id: PageElementIdSchema,
  parent: PageElementIdSchema.nullable(),
  kind: z.enum(["text", "media", "control", "group"]),
  tag: z.string(),
  /** Its first three classes. */
  classes: z.array(z.string()),
  /** The text a shopper is shown; a picture's alt text. */
  text: z.string(),
  /** Its DOM text, hidden parts included. */
  fullText: z.string(),
  /**
   * A render measures it at 375 px. A served read says false only when markup
   * hides it, and null otherwise.
   */
  shown: z.boolean().nullable(),
  href: z.string().nullable(),
  media: z
    .object({
      kind: z.enum(["image", "video", "embed"]),
      file: z.string(),
    })
    .nullable(),
  /** Its place in the nearest repeating ancestor, slider clones left out. */
  set: z.object({ size: z.number(), index: z.number() }).nullable(),
  widget: PageWidgetSchema.nullable(),
  /** Whether the served page held it, or a script drew it after load. */
  from: z.enum(["served", "afterLoad"]),
  /** The handle of the product card it sits in. */
  card: z.string().nullable(),
  /** The product it speaks of, with the evidence. Null is a store fact. */
  about: z
    .object({
      handle: z.string(),
      by: z.enum(["card", "link", "title", "buyBox"]),
    })
    .nullable(),
  description: DescriptionSchema,
  /** Its own id when the read holds it once. A hint for Viv, never checked further. */
  idHint: z.string().nullable(),
});

export const PopupSchema = z.object({
  /** `popup:<n>`, in the order the read found them; a section reading's id for it. */
  id: z.string(),
  kind: z.enum([
    "emailCapture",
    "discount",
    "ageGate",
    "cookie",
    "country",
    "other",
  ]),
  text: z.string(),
  elements: z.array(PageElementIdSchema),
  /** When it first showed in a render; `unknown` on a served read. */
  shown: z.enum(["atLoad", "afterScroll", "atCapture", "unknown"]),
  /** The section it was drawn in, which no longer holds its elements. */
  inSection: z.string().nullable(),
  /** The popup as a section reading reads it, as a section's `text`. */
  sectionText: z.string(),
});

export const PageHintTypeList = [
  "rating",
  "starIcons",
  "struckPrice",
  "countdown",
  "stockLevel",
  "disclosure",
  "breadcrumb",
  "icon",
  "structuredData",
] as const;

/**
 * A fact an element's markup states beside its words: a rating in an
 * aria-label, a run of star icons, a struck-through price. It tells whoever
 * judges the page what to look at, and is never a finding: the judge may
 * overrule it.
 */
export const PageHintSchema = z.object({
  element: PageElementIdSchema,
  type: z.enum(PageHintTypeList),
  /** Where it was read: an attribute, a tag, a class, a file, or `ld+json`. */
  from: z.string(),
  text: z.string(),
});

/** What a render gained over the served page, and how long each part took. */
export const RenderNoteSchema = z.object({
  ms: z.object({
    session: z.number(),
    goto: z.number(),
    domContentLoaded: z.number(),
    settle: z.number(),
    read: z.number(),
  }),
  bytes: z.number(),
  gained: z.array(
    z.object({
      section: z.string(),
      chars: z.number(),
      widgets: z.array(PageWidgetSchema),
    })
  ),
});

export const SectionReadSchema = z.object({
  /** The wrapper's id, `shopify-section-...`. */
  id: z.string(),
  /** Its top-level elements, in order. */
  elements: z.array(PageElementIdSchema),
  /** The handles its elements are about. */
  products: z.array(z.string()),
  /** Where a render laid it out at the phone fold, in page pixels. Null on a served read. */
  box: z
    .object({
      top: z.number(),
      left: z.number(),
      width: z.number(),
      height: z.number(),
    })
    .nullable(),
  /** Whether it stays on screen as the page scrolls. Null on a served read. */
  fixed: z.boolean().nullable(),
  /** What a shopper sees here without a tap, written for a model to read. */
  text: z.string(),
});

/**
 * One storefront page read element by element, from the served body or a
 * render of it, or from the theme's template files when neither could be read.
 * The read's own source owns the sections and their roles.
 */
export const PageReadSchema = z.object({
  version: z.literal(PAGE_READ_VERSION),
  /** The served page, after redirects. */
  url: z.string(),
  source: z.enum(["html", "rendered", "template"]),
  /**
   * What this read could check. A render checked all three; a served page
   * holds elements but not where they sit or what they show; a template read
   * holds no elements.
   */
  checked: z.object({
    elements: z.boolean(),
    position: z.boolean(),
    facts: z.boolean(),
  }),
  /** The template its own sections come from, `template--<id>__…`. Null when none does. */
  templateId: z.string().nullable(),
  /** The page a render laid out at the phone fold. Null when not rendered. */
  layout: z
    .object({
      viewportWidth: z.number(),
      viewportHeight: z.number(),
      pageHeight: z.number(),
    })
    .nullable(),
  declared: z.object({
    themeId: z.number().nullable(),
    themeRole: z.string().nullable(),
    ownSectionIds: z.array(z.string()),
    productId: z.number().nullable(),
    currency: z.string().nullable(),
    language: z.string().nullable(),
  }),
  /** The served structure: roles, what builds the page, the sitewide split. */
  analysis: PageAnalysisSchema,
  sections: z.array(SectionReadSchema),
  /** A page builder's content outside any section. */
  main: z
    .object({
      elements: z.array(PageElementIdSchema),
      products: z.array(z.string()),
      /** As a section's `text`. */
      text: z.string(),
    })
    .nullable(),
  elements: z.array(PageElementSchema),
  hints: z.array(PageHintSchema),
  popups: z.array(PopupSchema),
  /** Null on a served read. */
  render: RenderNoteSchema.nullable(),
  /** True when the page held more elements than a read keeps. */
  cut: z.boolean(),
});

export type PageWidgetType = z.infer<typeof PageWidgetSchema>;
export type DescriptionType = z.infer<typeof DescriptionSchema>;
export type PageElementType = z.infer<typeof PageElementSchema>;
export type PopupType = z.infer<typeof PopupSchema>;
export type PageHintType = z.infer<typeof PageHintSchema>;
export type RenderNoteType = z.infer<typeof RenderNoteSchema>;
export type SectionReadType = z.infer<typeof SectionReadSchema>;
export type PageReadType = z.infer<typeof PageReadSchema>;
